import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bot,
  Loader2,
  CheckCircle2,
  Folder,
  FileText,
  Sparkles,
  Wrench,
  ChevronRight,
  Brain,
  Terminal,
  Users,
  ExternalLink,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = process.env.REACT_APP_BACKEND_URL;

const TIMELINE_ICON = {
  phase_start: { icon: Users, color: "text-violet-600" },
  plan_ready: { icon: Sparkles, color: "text-violet-600" },
  step_start: { icon: ChevronRight, color: "text-blue-500" },
  tool_result: { icon: Wrench, color: "text-emerald-600" },
  done: { icon: CheckCircle2, color: "text-emerald-700" },
  finished: { icon: CheckCircle2, color: "text-emerald-700" },
  review_failed: { icon: AlertCircle, color: "text-amber-700" },
  system: { icon: Sparkles, color: "text-zinc-500" },
  warning: { icon: AlertCircle, color: "text-amber-600" },
  error: { icon: AlertCircle, color: "text-red-600" },
};

function lang(filename) {
  const ext = (filename.split(".").pop() || "").toLowerCase();
  return (
    {
      py: "python",
      js: "javascript",
      jsx: "javascript",
      ts: "typescript",
      tsx: "typescript",
      json: "json",
      html: "html",
      css: "css",
      md: "markdown",
      yml: "yaml",
      yaml: "yaml",
      sh: "shell",
      sql: "sql",
    }[ext] || "text"
  );
}

export default function AgentBuildPublicPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [build, setBuild] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/v1/agent-build/${token}`)
      .then((r) => {
        setBuild(r.data);
        const firstNonEmpty = (r.data.files || []).find(
          (f) => f.content && f.content.length > 0,
        );
        setSelectedFile(firstNonEmpty || (r.data.files || [])[0] || null);
      })
      .catch((e) => {
        setError(e.response?.status === 404 ? "Build not found or no longer public." : "Failed to load build.");
      });
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-200 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">{error}</h1>
          <Button onClick={() => navigate("/")} variant="outline" data-testid="public-go-home-btn">
            Go to home
          </Button>
        </div>
      </div>
    );
  }

  if (!build) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-200 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-build-page">
      {/* Top bar */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xs text-zinc-500">Built autonomously by</div>
              <div className="text-sm font-semibold">LLM Hub Agents</div>
            </div>
          </div>
          <Button
            onClick={() => navigate("/")}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="public-cta-btn"
          >
            Re-run on your own LLM Hub
            <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Hero */}
        <Card className="bg-zinc-900 border-zinc-800 p-6">
          <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
            {build.mode === "swarm" ? (
              <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20">
                <Users className="w-2.5 h-2.5 mr-1" />
                swarm pipeline
              </Badge>
            ) : (
              <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700">single-agent</Badge>
            )}
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              <CheckCircle2 className="w-2.5 h-2.5 mr-1" />
              completed
            </Badge>
            <span className="ml-2">
              <Clock className="w-3 h-3 inline mr-1" />
              {build.completed_at && new Date(build.completed_at).toLocaleDateString()}
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3 text-white">
            {build.task}
          </h1>
          <div className="flex flex-wrap gap-4 text-xs text-zinc-400">
            <span>
              <strong className="text-zinc-200">{build.steps_taken}</strong> steps
            </span>
            <span>
              <strong className="text-zinc-200">{build.tokens_used.toLocaleString()}</strong> tokens
            </span>
            <span>
              <strong className="text-zinc-200">{(build.files || []).length}</strong> files generated
            </span>
            <span>by {build.owner_email}</span>
          </div>
          {build.summary && (
            <p className="mt-4 text-sm text-zinc-300 leading-relaxed border-l-2 border-violet-500 pl-4">
              {build.summary}
            </p>
          )}
        </Card>

        {/* Plan (swarm only) */}
        {build.plan && build.plan.steps && build.plan.steps.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="public-plan-card">
            <h2 className="text-sm font-semibold text-zinc-300 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-500" />
              The plan (generated by Gemma 4)
            </h2>
            <div className="space-y-3">
              {build.plan.steps.map((s, i) => (
                <div key={s.id ?? i} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-300 text-xs font-mono flex items-center justify-center">
                    {s.id ?? i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-100">{s.title}</div>
                    {s.description && (
                      <div className="text-xs text-zinc-400 mt-0.5">{s.description}</div>
                    )}
                    {s.deliverable && (
                      <div className="text-xs text-emerald-400 mt-0.5 font-mono">→ {s.deliverable}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {build.plan.deliverables && build.plan.deliverables.length > 0 && (
              <div className="mt-5 pt-4 border-t border-zinc-800">
                <div className="text-xs text-zinc-500 mb-2">Deliverables</div>
                <div className="flex flex-wrap gap-1.5">
                  {build.plan.deliverables.map((d, i) => (
                    <code key={i} className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-emerald-400">
                      {d}
                    </code>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Files browser */}
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="public-files-card">
          <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-2">
            <Folder className="w-4 h-4 text-zinc-400" />
            <h2 className="text-sm font-semibold text-zinc-200">Generated code</h2>
            <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 ml-auto">
              {(build.files || []).length} files
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 h-[500px]">
            <ScrollArea className="border-r border-zinc-800 bg-zinc-950/50">
              {(build.files || []).length === 0 ? (
                <div className="p-6 text-xs text-zinc-500">No files.</div>
              ) : (
                build.files.map((f) => (
                  <button
                    key={f.path}
                    onClick={() => setSelectedFile(f)}
                    className={`block w-full text-left px-4 py-2 text-xs font-mono border-b border-zinc-900 hover:bg-zinc-800/50 truncate ${
                      selectedFile?.path === f.path ? "bg-zinc-800 text-violet-400" : "text-zinc-300"
                    }`}
                    data-testid={`public-file-${f.path.replace(/[^a-z0-9]/gi, "-")}`}
                  >
                    <FileText className="w-3 h-3 inline mr-2" />
                    {f.path}
                    <span className="text-zinc-600 ml-2">{f.size}b</span>
                  </button>
                ))
              )}
            </ScrollArea>
            <ScrollArea className="md:col-span-2">
              {!selectedFile ? (
                <div className="h-full flex items-center justify-center text-xs text-zinc-500">
                  Select a file to preview
                </div>
              ) : (
                <div>
                  <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between bg-zinc-950/30 sticky top-0">
                    <code className="text-xs text-zinc-300 font-mono">{selectedFile.path}</code>
                    <Badge className="bg-zinc-800 text-zinc-500 border-zinc-700 text-[10px]">
                      {lang(selectedFile.path)}
                    </Badge>
                  </div>
                  <pre className="text-xs font-mono p-4 whitespace-pre-wrap break-words text-zinc-200 leading-relaxed">
                    {selectedFile.content || "(file too large to preview inline)"}
                  </pre>
                </div>
              )}
            </ScrollArea>
          </div>
        </Card>

        {/* Timeline */}
        {build.timeline && build.timeline.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="public-timeline-card">
            <h2 className="text-sm font-semibold text-zinc-300 mb-4 flex items-center gap-2">
              <Terminal className="w-4 h-4 text-violet-500" />
              Live activity ({build.timeline.length} events)
            </h2>
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2">
              {build.timeline.map((evt, i) => {
                const cfg = TIMELINE_ICON[evt.type] || TIMELINE_ICON.system;
                const Icon = cfg.icon;
                let body = evt.message || evt.summary || "";
                if (evt.type === "step_start") {
                  body = `Step ${evt.step} · ${evt.role} · ${evt.model}${evt.phase && evt.phase !== "single" ? ` [${evt.phase}]` : ""}`;
                } else if (evt.type === "tool_result") {
                  const args = evt.args || {};
                  const argDisp = args.path || args.command || args.url || "";
                  body = `${evt.tool}${argDisp ? ` → ${argDisp}` : ""} ${evt.result?.ok ? "✓" : "✗"}`;
                } else if (evt.type === "phase_start") {
                  body = `${(evt.phase || "").toUpperCase()} phase started${evt.model ? ` · ${evt.model}` : ""}`;
                } else if (evt.type === "plan_ready") {
                  body = `Plan ready · ${evt.steps} steps`;
                } else if (evt.type === "finished") {
                  body = `${evt.status} · ${evt.steps} steps · ${evt.tokens} tokens`;
                }
                return (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <Icon className={`w-3 h-3 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                    <span className="text-zinc-500 font-mono w-16 flex-shrink-0">
                      {evt.ts ? new Date(evt.ts).toLocaleTimeString() : ""}
                    </span>
                    <span className="font-mono text-zinc-400 w-24 flex-shrink-0 truncate">{evt.type}</span>
                    <span className="text-zinc-300 truncate">{body}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Footer CTA */}
        <Card className="bg-gradient-to-br from-violet-900/30 to-fuchsia-900/20 border-violet-500/30 p-8 text-center">
          <Brain className="w-10 h-10 text-violet-400 mx-auto mb-3" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Built by a local model on a single 31GB VPS
          </h3>
          <p className="text-sm text-zinc-300 mb-5 max-w-2xl mx-auto">
            Zero API cost. Zero rate limits. Open code, your hardware. Spin up your own LLM Hub
            and let agents ship code while you sleep.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="public-cta-footer-btn"
          >
            Try it yourself
            <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </Button>
        </Card>
      </div>

      <footer className="border-t border-zinc-900 mt-12 py-6 text-center text-xs text-zinc-600">
        Built autonomously · Powered by local Gemma 4 + Qwen 2.5 Coder · Read-only public view
      </footer>
    </div>
  );
}
